import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridApiRef,
  GridFilterModel,
  GridFilterItem,
  GridSortModel,
  gridClasses,
  useGridApiRef,
} from '@mui/x-data-grid'
import {
  Box,
  Portal,
  Paper,
  Menu,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import PrintIcon from '@mui/icons-material/Print'
import PrintableDataGrid from './PrintableDataGrid'
import RestoreIcon from '@mui/icons-material/Restore'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingState, EmptyState, ErrorBoundary } from '../Common'
import { useTableStateManager, useEnhancedExport, useFilteredData, useStickyHorizontalScrollbar } from '../../hooks'
import { treasurySpanishLocale } from '../../utils/localization'
import { CustomToolbar, useDataGridSettings } from './CustomToolbar'



export interface TreasuryDataGridProps extends Omit<DataGridProps, 'columns'> {
  columns: GridColDef[]
  title?: string
  subtitle?: string
  showToolbar?: boolean
  showExport?: boolean
  showRefresh?: boolean
  showPrint?: boolean
  enableAutoHeight?: boolean
  minHeight?: number
  maxHeight?: number
  loading?: boolean
  /** Keep existing rows mounted during a refresh to preserve the grid and scroll. */
  keepRowsWhileLoading?: boolean
  error?: string | null
  emptyMessage?: string
  onExport?: (format: 'csv' | 'excel') => void
  onRefresh?: () => void
  customActions?: React.ReactNode
  density?: 'compact' | 'standard' | 'comfortable'
  /** Unique identifier for state persistence */
  tableId?: string
  /** Enable state persistence */
  enableStatePersistence?: boolean
  /** Show column customization options */
  showColumnCustomization?: boolean
  /** Export configuration */
  exportOptions?: {
    filename?: string
    includeHeader?: boolean
    includeFooter?: boolean
    includeSummary?: boolean
  }
  /** Use custom toolbar with enhanced features */
  useCustomToolbar?: boolean
  /** Show filter bar in custom toolbar */
  showFilterBar?: boolean
  /** Show settings menu in custom toolbar */
  showSettings?: boolean
  /** Callback when filtered data changes */
  onFilteredDataChange?: (tableId: string, filteredData: any[]) => void
  /** Enable a sticky (viewport-fixed) horizontal scrollbar for wide grids */
  enableStickyHorizontalScroll?: boolean
  /** Offset from the bottom of the viewport for the sticky scrollbar (e.g. floating toolbar height) */
  stickyScrollBottomOffset?: number
  /** Whether this grid is the active one (useful for tab-based layouts with multiple grids) */
  stickyScrollIsActive?: boolean
  /**
   * Keep the `checkboxSelection` column visible while the grid scrolls sideways.
   *
   * Only meaningful together with `checkboxSelection`, and off by default so no existing grid
   * changes behaviour. On a wide table the checkbox otherwise scrolls out of view exactly when
   * you most need to know which row you are ticking.
   *
   * Column pinning proper is a Pro feature -- `pinnedColumns` does not exist in the Community
   * package we use, and neither does `useGridColumnPinning`. What Community *does* ship is the
   * styling for pinned columns, so this reproduces it with CSS for the one column whose offset
   * is knowable without Pro's machinery: the first one, always at `left: 0`.
   *
   * **It also turns virtualisation off for this grid**, because CSS alone cannot pin a column
   * that the virtualiser unmounts. That is a real cost -- see the note by
   * `disableVirtualization` below -- so turn this on for a grid whose page size stays modest,
   * not for one that renders hundreds of rows at a time.
   */
  enableStickyCheckboxColumn?: boolean
  /**
   * Sort applied when there is nothing saved for this table.
   *
   * `initialState.sorting` does not work here and never did: `sortModel` is a controlled
   * prop, and MUI ignores `initialState` for anything it controls. Use this instead.
   */
  defaultSort?: GridSortModel
  /**
   * Start from `defaultSort` on every mount, ignoring whatever order was saved before.
   *
   * For a table whose default order is part of how it is meant to be read rather than a
   * per-person preference. Sorting still works normally during the session.
   */
  forceDefaultSortOnMount?: boolean
}

/**
 * The "no sort" default, as a module-level constant rather than an inline `[]`.
 *
 * A fresh array each render would change identity every time and defeat the `useMemo` that
 * builds the state-manager options, rebuilding them on every render for every grid.
 */
const SIN_ORDEN: GridSortModel = []

/**
 * Keeps the checkbox column in place while the grid scrolls sideways.
 *
 * `position: sticky` inside the virtual scroller, which works because nothing between the
 * cell and that scroller establishes another containing block -- the sticky horizontal
 * scrollbar this component can also render only mirrors `scrollLeft`, it does not wrap the
 * grid in a second overflow container.
 *
 * The z-indexes are MUI's own values for pinned columns (30 for cells, 40 for headers), so a
 * pinned checkbox lands above the column separator and below the header, exactly where Pro
 * would put it.
 *
 * **The backgrounds are the part that is easy to get wrong.** A sticky cell with no background
 * lets the other columns slide visibly underneath it. An opaque colour fixes the resting
 * state, but hover and selection in MUI are *translucent overlays* -- painting those directly
 * would make the cell see-through again at the exact moment somebody is interacting with it.
 * So the overlay goes on as a `linear-gradient` image on top of an opaque `backgroundColor`:
 * the two composite to an opaque result, and it matches how the rest of the row is painted.
 */
const estilosCasillaFija = {
  // The grid's own colour variables, not invented ones: `--DataGrid-t-cell-background-pinned`
  // is what MUI paints its pinned cells with, so a pinned checkbox matches a pinned column
  // for free and follows the theme. Each carries a fallback anyway -- an unknown CSS variable
  // with no fallback resolves to nothing and the cell would silently go transparent, which is
  // the one failure this whole block exists to prevent.
  [`& .${gridClasses.columnHeaderCheckbox}`]: {
    position: 'sticky',
    left: 0,
    zIndex: 40,
    backgroundColor: (theme: any) =>
      `var(--DataGrid-t-header-background-base, ${theme.palette.background.paper})`,
  },
  [`& .${gridClasses.cellCheckbox}`]: {
    position: 'sticky',
    left: 0,
    zIndex: 30,
    backgroundColor: (theme: any) =>
      `var(--DataGrid-t-cell-background-pinned, ${theme.palette.background.paper})`,
  },
  [`& .${gridClasses.row}:hover .${gridClasses.cellCheckbox}`]: {
    backgroundImage: (theme: any) =>
      `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
  },
  [`& .${gridClasses.row}.Mui-selected .${gridClasses.cellCheckbox}`]: {
    backgroundImage: (theme: any) =>
      `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
  },
  [`& .${gridClasses.row}.Mui-selected:hover .${gridClasses.cellCheckbox}`]: {
    backgroundImage: (theme: any) =>
      `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected}), `
      + `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
  },
}

export const TreasuryDataGrid: React.FC<TreasuryDataGridProps> = ({
  columns,
  title,
  subtitle,
  showToolbar = true,
  showExport = false,
  showRefresh = false,
  showPrint = false,
  enableAutoHeight = false,
  minHeight = 400,
  maxHeight = 800,
  loading = false,
  keepRowsWhileLoading = false,
  error = null,
  emptyMessage,
  onExport,
  onRefresh,
  customActions,
  density = 'compact',
  rows = [],
  tableId,
  enableStatePersistence = true,
  showColumnCustomization = true,
  exportOptions = {},
  useCustomToolbar = true,
  showFilterBar = true,
  showSettings = true,
  onFilteredDataChange,
  enableStickyHorizontalScroll = false,
  enableStickyCheckboxColumn = false,
  defaultSort = SIN_ORDEN,
  forceDefaultSortOnMount = false,
  stickyScrollBottomOffset = 0,
  stickyScrollIsActive = true,
  ...otherProps
}) => {
  const apiRef = useGridApiRef()
  const containerRef = useRef<HTMLDivElement>(null)

  // Sticky horizontal scrollbar for wide grids (opt-in via enableStickyHorizontalScroll)
  const {
    stickyScrollbarRef,
    isVisible: stickyIsVisible,
    scrollWidth: stickyScrollWidth,
    leftPosition: stickyLeftPosition,
    containerWidth: stickyContainerWidth,
  } = useStickyHorizontalScrollbar({
    enabled: enableStickyHorizontalScroll,
    bottomOffset: stickyScrollBottomOffset,
    containerRef,
    isActive: stickyScrollIsActive,
    refreshKey: rows.length, // re-init the sticky bar once async data arrives (0 -> N)
  })
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null)
  const [showPrintDialog, setShowPrintDialog] = useState(false)

  // Custom toolbar settings
  const [toolbarSettings, setToolbarSettings] = useDataGridSettings(
    `${tableId || title?.replace(/\s+/g, '-').toLowerCase() || 'default'}-toolbar-settings`
  )

  // Memoize table ID to prevent state manager recreation
  const stableTableId = useMemo(() => 
    tableId || `treasury-grid-${title?.replace(/\s+/g, '-').toLowerCase() || 'default'}`, 
    [tableId, title]
  )

  // State management hook with memoized options
  const stateManagerOptions = useMemo(() => ({
    tableId: stableTableId,
    defaultPagination: { page: 0, pageSize: 25 },
    // Was hardcoded to []. Note that `initialState.sorting` does NOT work on this component
    // and never did: `sortModel` below is a controlled prop, and MUI ignores `initialState`
    // for anything it controls. This is the only way in.
    defaultSort,
    defaultFilter: { items: [] },
    defaultDensity: density,
    enablePersistence: enableStatePersistence,
    forceDefaultSortOnMount,
  }), [stableTableId, density, enableStatePersistence, defaultSort, forceDefaultSortOnMount])

  const stateManager = useTableStateManager(stateManagerOptions)


  // Filtered data hook
  const { handleFilterModelChange } = useFilteredData({
    tableId: stableTableId,
    originalData: rows,
    onFilteredDataChange,
  })

  // Initial data callback to ensure stats are populated on mount
  useEffect(() => {
    if (onFilteredDataChange) {
      // Small delay to ensure DataGrid is properly initialized
      const timer = setTimeout(() => {
        onFilteredDataChange(stableTableId, rows)
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [stableTableId, rows, onFilteredDataChange])

  // Filter removal handler for custom toolbar
  const handleRemoveFilter = useCallback((filterId: GridFilterItem['id']) => {
    const currentFilter = stateManager.state.filter
    const newFilterModel: GridFilterModel = {
      items: currentFilter.items.filter((item) => item.id !== filterId),
    }
    
    // Update local state
    stateManager.setFilter(newFilterModel)
    
    // Update filtered data
    handleFilterModelChange(newFilterModel, apiRef)
  }, [stateManager, handleFilterModelChange])

  // Enhanced export hook
  const { exportData } = useEnhancedExport()

  // Combined filter change handler
  const handleFilterChange = useCallback((filterModel: GridFilterModel) => {
    // Update local state manager
    stateManager.setFilter(filterModel)
    
    // Update filtered data
    handleFilterModelChange(filterModel, apiRef)
  }, [stateManager, handleFilterModelChange])


  // Use original columns without custom width management
  const columnsWithWidths = useMemo(() => {
    return columns
  }, [columns])


  // Memoize DataGrid props to prevent unnecessary re-renders
  const memoizedDataGridProps = useMemo(() => {
    const baseProps = {
      pageSizeOptions: [10, 25, 50, 100],
      autoHeight: enableAutoHeight,
      disableRowSelectionOnClick: true,
      disableColumnResize: false, // Enable MUI default column resizing
      disableColumnSelector: false, // Enable column selector
      disableColumnFilter: false, // Enable column filtering
      disableColumnMenu: false, // Enable column menu
      showToolbar: showToolbar,
      // Virtualisation off, and nothing less will do. The grid virtualises COLUMNS as well as
      // rows -- GridRow renders only `firstColumnIndex..lastColumnIndex` -- so a column that
      // scrolls out of view is *unmounted*, and `position: sticky` cannot hold an element
      // that is no longer in the DOM. Pro renders pinned columns outside that loop, which is
      // the mechanism Community lacks.
      //
      // `columnBufferPx` was tried first and is not enough, for a reason worth recording:
      // `bufferForDirection` in useGridVirtualScroller.js returns `columnBefore: 0` while the
      // scroll direction is RIGHT, and only honours `columnBufferPx` when the direction is
      // NONE. So during the drag everything behind the viewport is dropped whatever the
      // buffer says, and the column reappears the instant you let go -- which is exactly the
      // flicker this replaces.
      //
      // The cost is bounded by the page size, not by the table: 25 rows x ~15 columns is
      // roughly 375 cells. Only for grids that ask for a sticky column; every other grid
      // keeps full virtualisation.
      ...(enableStickyCheckboxColumn ? { disableVirtualization: true } : {}),
    }

    if (useCustomToolbar) {
      return {
        ...baseProps,
        slots: {
          toolbar: CustomToolbar,
        },
        slotProps: {
          toolbar: {
            settings: toolbarSettings,
            onSettingsChange: setToolbarSettings,
            onRemoveFilter: handleRemoveFilter,
            showFilterBar,
            showSettings,
            customTitle: title || 'DataGrid',
          },
        },
      }
    }

    return {
      ...baseProps,
      slotProps: {
        toolbar: {
          showQuickFilter: true,
          quickFilterProps: {
            debounceMs: 300,
          },
          printOptions: { disableToolbarButton: true },
        },
      },
    }
  }, [
    enableAutoHeight, showToolbar, useCustomToolbar, toolbarSettings,
    setToolbarSettings, handleRemoveFilter, showFilterBar, showSettings, title,
    enableStickyCheckboxColumn
  ])

  // Memoize DataGrid styles - theme handles border styling
  const gridStyles = useMemo(() => ({
    border: 'none', // Theme will provide the main border
    // Let theme handle all border styling via MUI DataGrid props
    ...(enableStickyCheckboxColumn ? estilosCasillaFija : {}),
  }), [enableStickyCheckboxColumn])

  // Enhanced export handler
  const handleEnhancedExport = async (format: 'csv' | 'excel') => {
    if (onExport) {
      // Use legacy export if provided
      onExport(format)
    } else {
      // Use enhanced export with branding
      await exportData(apiRef, columnsWithWidths, [...rows], {
        format,
        filename: exportOptions.filename || `${title?.replace(/\s+/g, '_').toLowerCase() || 'export'}_${new Date().toISOString().split('T')[0]}`,
        header: exportOptions.includeHeader !== false ? {
          company: 'Automatización y Control S.A.S.',
          department: 'Gerencia - Sistema de Control de Costos - Lotes',
          title,
          subtitle,
          reportDate: new Date().toLocaleDateString('es-CO'),
        } : undefined,
        footer: exportOptions.includeFooter !== false ? {
          generatedBy: 'Arquitectura y Concreto - Ingenieria Digital',
          timestamp: new Date().toLocaleString('es-CO'),
          totalRecords: rows.length,
        } : undefined,
        includeSummary: exportOptions.includeSummary,
        summaryCalculations: exportOptions.includeSummary ? {
          // Auto-detect currency fields for sum calculation
          ...Object.fromEntries(
            columnsWithWidths
              .filter(col => 
                col.field.toLowerCase().includes('valor') ||
                col.field.toLowerCase().includes('monto') ||
                col.field.toLowerCase().includes('saldo')
              )
              .map(col => [col.field, 'sum' as const])
          )
        } : undefined,
      })
    }
  }

  // Settings menu handlers
  const handleSettingsClose = () => {
    setSettingsMenuAnchor(null)
  }

  const handleExportClose = () => {
    setExportMenuAnchor(null)
  }

  // Memoize grid height calculation to prevent flickering
  const gridHeight = useMemo(() => {
    return enableAutoHeight 
      ? 'auto' 
      : Math.min(Math.max(minHeight, (rows.length + 2) * 52 + 100), maxHeight)
  }, [enableAutoHeight, minHeight, maxHeight, rows.length])

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 4 }}>
        <EmptyState
          type="no-data"
          title="Error al cargar datos"
          message={error}
          action={
            onRefresh
              ? {
                  label: 'Reintentar',
                  onClick: onRefresh,
                  variant: 'contained',
                }
              : undefined
          }
        />
      </Paper>
    )
  }

  if (loading && (!keepRowsWhileLoading || rows.length === 0)) {
    return (
      <Paper elevation={1} sx={{ height: gridHeight }}>
        <LoadingState type="table" />
      </Paper>
    )
  }

  const dataGridElement = (
    <DataGrid
      apiRef={apiRef}
      loading={loading}
      columns={columnsWithWidths}
      rows={rows}
      density={useCustomToolbar ? (toolbarSettings.density || 'compact') : stateManager.state.density}
      showCellVerticalBorder={useCustomToolbar ? toolbarSettings.showCellBorders : false}
      showColumnVerticalBorder={useCustomToolbar ? toolbarSettings.showColumnBorders : false}
      paginationModel={stateManager.state.pagination}
      onPaginationModelChange={stateManager.setPagination}
      sortModel={stateManager.state.sort}
      onSortModelChange={stateManager.setSort}
      filterModel={stateManager.state.filter}
      onFilterModelChange={handleFilterChange}
      columnVisibilityModel={stateManager.state.columnVisibility}
      onColumnVisibilityModelChange={stateManager.setColumnVisibility}
      localeText={treasurySpanishLocale}
      sx={gridStyles}
      {...memoizedDataGridProps}
      {...otherProps}
    />
  )

  return (
    <ErrorBoundary>
      <Paper
        ref={containerRef}
        elevation={1}
        sx={{
          height: gridHeight,
          overflow: 'hidden',
          ...(enableAutoHeight && {
            height: 'auto',
            minHeight: minHeight,
          }),
        }}
      >
        {rows.length === 0 ? (
          <EmptyState
            type="no-data"
            message={emptyMessage || 'No hay datos para mostrar'}
            height={400}
          />
        ) : (
          dataGridElement
        )}
      </Paper>

      {/* Sticky (viewport-fixed) horizontal scrollbar for wide grids.
          Portaled to document.body so no transformed/filtered ancestor (e.g. a Card
          with `:hover { transform }`) can become its containing block and break
          `position: fixed` — which would make the bar jump off-screen on hover. */}
      {enableStickyHorizontalScroll && (
        <Portal>
          <Box
            ref={stickyScrollbarRef}
            sx={{
              position: 'fixed',
              bottom: stickyScrollBottomOffset,
              left: stickyLeftPosition,
              width: stickyContainerWidth,
              height: '17px',
              overflowX: 'auto',
              overflowY: 'hidden',
              zIndex: 1200,
              display: stickyIsVisible ? 'block' : 'none',
            }}
          >
            <Box sx={{ height: '1px', width: stickyScrollWidth }} />
          </Box>
        </Portal>
      )}

        {/* Export Menu */}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { handleEnhancedExport('csv'); handleExportClose(); }}>
            <ListItemIcon>
              <GetAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exportar como CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleEnhancedExport('excel'); handleExportClose(); }}>
            <ListItemIcon>
              <PrintIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exportar como Excel</ListItemText>
          </MenuItem>
        </Menu>

        {/* Settings Menu */}
        <Menu
          anchorEl={settingsMenuAnchor}
          open={Boolean(settingsMenuAnchor)}
          onClose={handleSettingsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { stateManager.setDensity('compact'); handleSettingsClose(); }}>
            <ListItemIcon>
              <ViewColumnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Vista Compacta</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { stateManager.setDensity('standard'); handleSettingsClose(); }}>
            <ListItemIcon>
              <ViewColumnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Vista Estándar</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { stateManager.setDensity('comfortable'); handleSettingsClose(); }}>
            <ListItemIcon>
              <ViewColumnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Vista Cómoda</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { stateManager.saveState(); handleSettingsClose(); }}>
            <ListItemIcon>
              <SaveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Guardar Configuración</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { stateManager.resetState(); handleSettingsClose(); }}>
            <ListItemIcon>
              <RestoreIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restaurar por Defecto</ListItemText>
          </MenuItem>
        </Menu>

        {/* Print Dialog */}
        <Dialog
          open={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Vista Previa de Impresión</DialogTitle>
          <DialogContent>
            <PrintableDataGrid
              title={title}
              subtitle={subtitle}
              columns={columnsWithWidths}
              rows={[...rows]}
              showPrintButton={false}
              headerInfo={{
                company: 'Automatización y Control S.A.S.',
                department: 'Gerencia de Proyectos - Sistema Control de Costos',
                reportDate: new Date().toLocaleDateString('es-CO'),
                generatedBy: 'Sistema AutoCE',
              }}
              footerInfo={{
                totalRecords: rows.length,
                timestamp: new Date().toLocaleString('es-CO'),
              }}
              summaryData={
                exportOptions.includeSummary ? [
                  ...Object.entries(
                    columnsWithWidths
                      .filter(col => 
                        col.field.toLowerCase().includes('valor') ||
                        col.field.toLowerCase().includes('monto') ||
                        col.field.toLowerCase().includes('saldo')
                      )
                      .reduce((acc, col) => {
                        const total = rows.reduce((sum, row) => {
                          const value = row[col.field]
                          const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value
                          return sum + (isNaN(numValue) ? 0 : numValue)
                        }, 0)
                        acc[col.field] = total
                        return acc
                      }, {} as Record<string, number>)
                  ).map(([field, total]) => {
                    const column = columnsWithWidths.find(col => col.field === field)
                    return {
                      label: `Total ${column?.headerName || field}`,
                      value: total,
                      formatted: new Intl.NumberFormat('es-CO', {
                        style: 'currency',
                        currency: 'COP',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(total)
                    }
                  })
                ] : []
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPrintDialog(false)}>
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
    </ErrorBoundary>
  );
}

export default TreasuryDataGrid